import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { assetsOrgAssetsCreate } from '@goldcast/api/content';
import { useClipAsset } from '../useClipAsset';
import { remotionPlayerStateStore } from '../../ClipNewTimeline/store/remotionPlayerState';
import { AddMenuItem } from '../../ClipNewTimeline/TimelinePlayerActionBar/AddMenu/types';
import { setIsAddingVideo, timelineActionsStore } from '../../ClipNewTimeline/store/timelineActions';
import { VideoOverlayTrack } from './types';
import { addToRecentlyUsed } from './videoOverlayLibraryUtils';
import { ASSET_PATH } from '../constants';
import { useMultiAssetUploader } from '@/components/molecules/MultiAssetUploader/useMultiAssetUploader';
import { VIDEO_FORMATS, VIDEO_MAX_SIZE } from '@/components/molecules/MultiAssetUploader/constants';
import { currentUser } from '@/stores/user';
import { getRelativePlayerTime } from '@/App/remotion/MainPlayer/utils/deletesUtils';
import { useAppContext } from '@/context/AppContext/AppContext';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { trackClipUpdate } from '@/stores/clip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { getVideoDurationFromSrc } from '@/libs/getVideoDuration';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

interface UseVideoOverlayProps {
  onUploadComplete?: () => void;
}

interface UseVideoOverlayReturn {
  // Core upload functionality
  uploadVideo: (files: File[]) => Promise<string | undefined>;
  acceptedFormats: string;

  // Library selection functionality
  selectLibraryVideo: (videoTrack: VideoOverlayTrack) => Promise<void>;

  // Timeline integration
  addVideoOverlayMenuItem: AddMenuItem;
}

/**
 * Custom hook for video overlay functionality
 * Provides unified upload logic for both popup and timeline flows
 */
export const useVideoOverlay = ({ onUploadComplete }: UseVideoOverlayProps): UseVideoOverlayReturn => {
  const { fps, clipId } = useClipsContext();
  const { addClipAsset } = useClipAsset();
  const { logger } = useAppContext();
  const { isAddingVideo } = useSyncExternalStore(timelineActionsStore.subscribe, timelineActionsStore.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isVideoOverlayModalEnabled = featureFlags[FeatureFlagKeys.Use_CL_Replace_Video_Overlays];

  const organization = currentUser.getSnapshot()?.organization;
  const baseFilePath = useMemo(() => `${organization}/clip-asset`, [organization]);
  const uploadedUrlRef = useRef<string | undefined>();

  // Calculate current frame from player state or use provided frame
  const getCurrentFrame = useCallback((): number => {
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
    return getRelativePlayerTime({ currentTime, deleteBounds: [] }) * fps - 1;
  }, [fps]);

  // Core business logic for processing uploaded assets
  const processUploadedAssets = useCallback(
    async (validFiles: any[], frame: number) => {
      try {
        await assetsOrgAssetsCreate({
          body: [
            {
              name: validFiles[0].filename,
              source_url: validFiles[0].url,
              type: 'VIDEO'
            }
          ],
          queryParams: {}
        });

        addClipAsset({
          currentFrame: frame,
          asset: validFiles[0],
          type: 'video'
        });

        trackClipUpdate(clipId, 'VIDEO_OVERLAY');
        showSuccessToast('Video uploaded successfully');
      } catch (error) {
        logger.error(error);
        showErrorToast('Failed to upload video');
        throw error;
      }
    },
    [addClipAsset, clipId, logger]
  );

  const { uploadFiles, acceptedFormats } = useMultiAssetUploader({
    baseFilePath,
    assetPath: ASSET_PATH,
    formats: VIDEO_FORMATS,
    maxSize: VIDEO_MAX_SIZE,
    handleUploadedAssets: async ({ validFiles }) => {
      if (validFiles.length > 0) {
        uploadedUrlRef.current = validFiles[0].url;
        const frame = getCurrentFrame();
        await processUploadedAssets(validFiles, frame);
      }
    },
    onError: error => {
      logger.error(error);
      showErrorToast('message' in error ? error.message : 'Failed to upload video');
    }
  });

  const uploadVideo = useCallback(
    async (files: File[]): Promise<string | undefined> => {
      if (!files.length) {
        return;
      }

      setIsAddingVideo(true);
      uploadedUrlRef.current = undefined;

      try {
        await uploadFiles(files);
        return uploadedUrlRef.current;
      } catch (error) {
        logger.error(error);
        showErrorToast('Failed to upload video');
        throw error;
      } finally {
        setIsAddingVideo(false);
        onUploadComplete?.();
      }
    },
    [uploadFiles, onUploadComplete, logger]
  );

  const selectLibraryVideo = useCallback(
    async (videoTrack: VideoOverlayTrack): Promise<void> => {
      setIsAddingVideo(true);

      try {
        const frame = getCurrentFrame();

        // Use duration from API (Pexels metadata) as primary source
        let duration = videoTrack.duration;

        // Fallback: If API doesn't provide duration, fetch from video URL
        if (!duration || duration === 0) {
          try {
            duration = await getVideoDurationFromSrc(videoTrack.videoUrl);
          } catch (error) {
            logger.error('Failed to get video duration:', error);
            // Continue without duration - it will be calculated later
          }
        }

        addClipAsset({
          currentFrame: frame,
          asset: {
            filename: videoTrack.name,
            url: videoTrack.videoUrl,
            type: 'video',
            name: videoTrack.name,
            duration: duration
          },
          type: 'video'
        });

        // Track recently used
        addToRecentlyUsed(videoTrack);

        // Analytics tracking
        trackClipUpdate(clipId, 'VIDEO_OVERLAY_LIBRARY_SELECT');

        showSuccessToast('Video added successfully');
        onUploadComplete?.();
      } catch (error) {
        logger.error('Failed to add library video:', error);
        showErrorToast('Failed to select video');
      } finally {
        setIsAddingVideo(false);
      }
    },
    [getCurrentFrame, addClipAsset, clipId, logger, onUploadComplete]
  );

  const addVideoOverlayMenuItem: AddMenuItem = useMemo(
    () => ({
      id: 'add-video-overlay',
      label: isAddingVideo ? 'Adding Video...' : 'Add Video',
      isDisabled: isAddingVideo,
      iconName: 'IconVideo',
      onClick: async () => {
        if (isVideoOverlayModalEnabled) {
          EventBus.dispatch(CustomEvents.OpenVideoOverlayLibraryModal);
          return;
        }

        return new Promise<void>((resolve, reject) => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = acceptedFormats;
          input.multiple = false;

          input.onchange = async e => {
            try {
              const files = Array.from((e.target as HTMLInputElement).files || []);
              await uploadVideo(files);
              resolve();
            } catch (error) {
              reject(error);
            }
          };

          input.click();
        });
      }
    }),
    [isAddingVideo, isVideoOverlayModalEnabled, acceptedFormats, uploadVideo]
  );

  return {
    uploadVideo,
    acceptedFormats,
    selectLibraryVideo,
    addVideoOverlayMenuItem
  };
};
